import React, { useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select from 'react-select';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  BulkPayoutResult,
  BulkPayoutRow,
  BulkPayoutRowStatus
} from '../../../../../shared/models/payout.model';
import { createBulkPayouts, previewBulkPayouts } from '../../../../shared/services/data.service';
import { getModuleName } from '../../../../shared/services/expenses.service';
import { getAllUnitTypes } from '../../../../../shared/services/data/unit-type.data';
import { birdToast } from '../../../../../shared/components/core-ui/toast';

export interface BulkPayoutModalProps {
  showModal: boolean;
  closeHandler: () => void;
  createdHandler: (result: BulkPayoutResult) => void;
}

type Source = 'pdf' | 'lines';

const SHOW_PASTE_LINES = false;
type IssueStatus = Exclude<BulkPayoutRowStatus, 'matched'>;
type EditableStatus = 'invalid_format' | 'not_found';
type BulkPayoutModalRow = BulkPayoutRow & { originalStatus?: EditableStatus };

interface RoomOption {
  value: string;
  label: string;
  unitTypeId: number;
}

const EDITABLE_STATUSES: BulkPayoutRowStatus[] = ['invalid_format', 'not_found'];

const ISSUES: Record<IssueStatus, { title: string; label: string }> = {
  invalid_format: { title: 'Missing unit code', label: 'e.g. Verdi-1C-103' },
  not_found: { title: 'No matching room', label: 'Check the unit code' },
  duplicate: { title: 'Duplicate unit code', label: 'Already in this batch' }
};

const amountFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const cellInputClassName =
  'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 focus:border-[#185B43] focus:outline-none focus:ring-4 focus:ring-[#185B43]/10';

const roomSelectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 42,
    borderRadius: 12,
    borderColor: state.isFocused ? '#185B43' : '#cbd5e1',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(24, 91, 67, 0.1)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#185B43' : '#94a3b8' }
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 14,
    backgroundColor: state.isSelected
      ? '#185B43'
      : state.isFocused
      ? '#f0fdf4'
      : base.backgroundColor
  }),
  singleValue: (base: any) => ({
    ...base,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 14,
    fontWeight: 600
  })
};

const repeatedRoomSelectStyles = {
  ...roomSelectStyles,
  control: (base: any, state: any) => ({
    ...roomSelectStyles.control(base, state),
    borderColor: '#f59e0b',
    '&:hover': { borderColor: '#d97706' }
  })
};

export const BulkPayoutModal: React.FC<BulkPayoutModalProps> = ({
  showModal,
  closeHandler,
  createdHandler
}) => {
  const [source, setSource] = useState<Source>('pdf');
  const [lines, setLines] = useState<string>('');
  const [file, setFile] = useState<File>();
  const [rows, setRows] = useState<BulkPayoutModalRow[] | null>(null);
  const [roomOptions, setRoomOptions] = useState<RoomOption[] | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeFile = source === 'pdf' ? file : undefined;
  const activeLines = source === 'lines' ? lines : '';
  const matchedRows = (rows || []).filter((row) => row.status === 'matched');
  const issueRows = (rows || []).filter((row) => row.status !== 'matched');
  const totalAmount = matchedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const repeatedRoomIds = new Set(
    matchedRows
      .filter((row, index) => matchedRows.findIndex((item) => item.roomId === row.roomId) !== index)
      .map((row) => row.roomId)
  );
  const canValidate = !isBusy && (!!activeFile || !!activeLines.trim());
  const canImport =
    !isBusy && matchedRows.length > 0 && matchedRows.every((row) => Number(row.amount) > 0);

  const reset = () => {
    setSource('pdf');
    setLines('');
    setFile(undefined);
    setRows(null);
    setIsBusy(false);
  };

  const cancel = () => {
    reset();
    closeHandler();
  };

  const changeSource = (next: Source) => {
    setSource(next);
    setRows(null);
  };

  const selectFile = (selected?: File) => {
    if (selected && selected.type !== 'application/pdf') {
      birdToast.error('Only PDF statements are supported');
      return;
    }
    setFile(selected);
    setRows(null);
  };

  const updateRow = (index: number, changes: Partial<BulkPayoutModalRow>) => {
    setRows((current) =>
      (current || []).map((row) => (row.index === index ? { ...row, ...changes } : row))
    );
  };

  const loadRoomOptions = () => {
    if (roomOptions) {
      return;
    }
    getAllUnitTypes(getModuleName(), 0, -1, {}).then((response) => {
      if (isErrorInfo(response)) {
        birdToast.error('Failed to load rooms');
        return;
      }
      setRoomOptions(
        (response.unitList || [])
          .flatMap((unitType) =>
            (unitType.units || []).map((unit) => ({
              value: unit.unitId,
              label: unit.shortName,
              unitTypeId: Number(unitType.id)
            }))
          )
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    });
  };

  const assignRoom = (row: BulkPayoutModalRow, option: RoomOption | null) => {
    const originalStatus = row.originalStatus || (row.status as EditableStatus);
    updateRow(
      row.index,
      option
        ? {
            status: 'matched',
            originalStatus,
            roomId: option.value,
            roomName: option.label,
            unitTypeId: option.unitTypeId
          }
        : {
            status: originalStatus,
            originalStatus: undefined,
            roomId: null,
            roomName: null,
            unitTypeId: null
          }
    );
  };

  const renderRoomSelect = (row: BulkPayoutModalRow) => (
    <Select<RoomOption, false>
      aria-label="Room"
      menuPortalTarget={document.body}
      styles={repeatedRoomIds.has(row.roomId) ? repeatedRoomSelectStyles : roomSelectStyles}
      options={roomOptions || []}
      value={(roomOptions || []).find((option) => option.value === row.roomId) || null}
      isClearable
      isLoading={!roomOptions}
      placeholder="Select room"
      onChange={(option) => assignRoom(row, option as RoomOption | null)}
    />
  );

  const validate = () => {
    setIsBusy(true);
    const promise = previewBulkPayouts(activeLines, activeFile)
      .then((response) => {
        if (isErrorInfo(response)) {
          throw response;
        }
        setRows(response.map((row) => ({ ...row, paymentDate: row.paymentDate || Date.now() })));
        if (response.some((row) => EDITABLE_STATUSES.includes(row.status))) {
          loadRoomOptions();
        }
        return response;
      })
      .finally(() => setIsBusy(false));

    birdToast.promise(promise, {
      loadingMessage: activeFile ? 'Validating ACH statement...' : 'Validating remittance lines...',
      successMessage: 'Validation complete',
      errorFailedMessage: activeFile
        ? 'Failed to validate ACH statement'
        : 'Failed to validate remittance lines',
      errorPermissionMessage: 'You do not have permission to create payouts'
    });
  };

  const importValid = () => {
    setIsBusy(true);
    const promise = createBulkPayouts(
      matchedRows.map((row) => ({
        index: row.index,
        page: row.page,
        reference: row.reference || '',
        roomId: row.originalStatus ? row.roomId : undefined,
        amount: Number(row.amount),
        paymentDate: row.paymentDate || Date.now(),
        description: row.description
      })),
      activeFile
    ).then((response) => {
      if (isErrorInfo(response)) {
        setIsBusy(false);
        throw response;
      }
      reset();
      createdHandler(response);
      return response;
    });

    birdToast.promise(promise, {
      loadingMessage: `Creating ${matchedRows.length} payouts...`,
      successMessage: 'Payouts created successfully',
      errorFailedMessage: 'Failed to create payouts',
      errorPermissionMessage: 'You do not have permission to create payouts'
    });
  };

  return (
    <Modal
      config={{
        showModal,
        fullScreen: false,
        maxWidth: 'md',
        contentClassName: 'flex min-h-0 flex-1 flex-col',
        paperClassName:
          '!flex !flex-col !overflow-hidden !rounded-3xl !border !border-slate-200 !shadow-[0_24px_60px_-12px_rgba(15,23,42,0.25)]'
      }}
      eventHandlers={{ closeModalHandler: cancel }}
    >
      <section
        aria-labelledby="bulk-payout-title"
        className="flex min-h-0 flex-1 flex-col bg-white text-slate-900 antialiased"
      >
        <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-8 py-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#185B43] text-white">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                viewBox="0 0 24 24"
              >
                <path
                  d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 id="bulk-payout-title" className="text-3xl font-bold tracking-tight text-slate-900">
              Bulk Payouts
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close dialog"
            onClick={cancel}
            className="rounded-full p-2.5 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-8 py-6">
          {SHOW_PASTE_LINES && (
            <div role="tablist" className="inline-flex rounded-full bg-slate-100 p-1">
              {(
                [
                  ['pdf', 'Upload PDF'],
                  ['lines', 'Paste lines']
                ] as [Source, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={source === value}
                  onClick={() => changeSource(value)}
                  className={`rounded-full px-5 py-2 text-base font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-[#185B43]/40 ${
                    source === value
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {source === 'pdf' && (
            <div
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragging(false);
                selectFile(event.dataTransfer.files?.[0]);
              }}
              className={`flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                isDragging
                  ? 'border-[#185B43] bg-green-50'
                  : 'border-slate-300 bg-slate-50 hover:border-slate-400'
              }`}
            >
              {file ? (
                <div className="flex w-full max-w-lg items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        clipRule="evenodd"
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate text-base font-semibold text-slate-900"
                      title={file.name}
                    >
                      {file.name}
                    </p>
                    <p className="text-sm text-slate-600">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-full px-3 py-1.5 text-sm font-semibold text-[#185B43] hover:bg-green-50"
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    aria-label="Remove file"
                    onClick={() => selectFile(undefined)}
                    className="rounded-full p-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-white text-[#185B43] shadow-sm">
                    <svg
                      className="h-7 w-7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.8}
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="text-base text-slate-700">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="font-semibold text-[#185B43] underline underline-offset-4 hover:text-[#134935]"
                    >
                      Choose ACH statement
                    </button>{' '}
                    or drop the PDF here
                  </p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(event) => {
                  selectFile(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
            </div>
          )}

          {source === 'lines' && (
            <div className="overflow-hidden rounded-2xl border-2 border-slate-300 bg-white transition-all focus-within:border-[#185B43] focus-within:ring-4 focus-within:ring-[#185B43]/10">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 font-mono text-sm text-slate-700">
                Description · Unit code · Amount · Date
              </div>
              <textarea
                aria-label="Remittance lines"
                rows={7}
                spellCheck={false}
                value={lines}
                placeholder="Accommodation For Verdi-1C-103 12500 01/09/2026"
                onChange={(event) => {
                  setLines(event.target.value);
                  setRows(null);
                }}
                className="w-full resize-y border-0 bg-transparent p-4 font-mono text-base leading-relaxed text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-0"
              />
            </div>
          )}

          {rows && (
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
                <p className="text-sm font-semibold text-emerald-800">Ready</p>
                <p className="text-2xl font-bold tabular-nums text-emerald-900">
                  {matchedRows.length}
                </p>
              </div>
              <div
                className={`rounded-2xl border px-5 py-4 ${
                  issueRows.length ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-slate-50'
                }`}
              >
                <p
                  className={`text-sm font-semibold ${
                    issueRows.length ? 'text-rose-800' : 'text-slate-700'
                  }`}
                >
                  Skipped
                </p>
                <p
                  className={`text-2xl font-bold tabular-nums ${
                    issueRows.length ? 'text-rose-900' : 'text-slate-900'
                  }`}
                >
                  {issueRows.length}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
                <p className="text-sm font-semibold text-slate-700">Total amount</p>
                <p className="text-2xl font-bold tabular-nums text-slate-900">
                  {amountFormatter.format(totalAmount)}
                </p>
              </div>
            </div>
          )}

          {issueRows.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-rose-200">
              <div className="flex items-center gap-2 bg-rose-50 px-5 py-3">
                <svg
                  className="h-5 w-5 text-rose-700"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h4 className="text-base font-semibold text-rose-900">
                  {issueRows.length} {issueRows.length === 1 ? 'line' : 'lines'} will be skipped
                </h4>
              </div>
              <ul className="divide-y divide-rose-100 bg-white">
                {issueRows.map((row) => (
                  <li key={row.index} className="flex items-center gap-4 px-5 py-3">
                    <span className="w-16 shrink-0 text-sm font-semibold text-slate-700">
                      {row.page ? `Page ${row.page}` : `Line ${row.index + 1}`}
                    </span>
                    <span
                      className="min-w-0 flex-1 truncate font-mono text-sm text-slate-900"
                      title={row.remittance}
                    >
                      {row.remittance || '—'}
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm font-semibold text-rose-800">
                        {ISSUES[row.status as IssueStatus].title}
                      </span>
                      <span className="block text-sm text-slate-600">
                        {ISSUES[row.status as IssueStatus].label}
                      </span>
                    </span>
                    {EDITABLE_STATUSES.includes(row.status) && (
                      <div className="w-56 shrink-0">{renderRoomSelect(row)}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {matchedRows.length > 0 && (
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full min-w-[720px] text-left text-base text-slate-800">
                <thead className="bg-slate-50 text-sm font-semibold text-slate-700">
                  <tr>
                    <th className="px-4 py-3">Room</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Pay day</th>
                    {matchedRows.some((row) => row.page) && <th className="px-4 py-3">Page</th>}
                  </tr>
                </thead>
                <tbody>
                  {matchedRows.map((row) => (
                    <tr key={row.index} className="border-t border-slate-200">
                      <td className="whitespace-nowrap px-4 py-3">
                        {row.originalStatus ? (
                          <div className="w-56">{renderRoomSelect(row)}</div>
                        ) : (
                          <span
                            className={`inline-flex rounded-lg px-2.5 py-1 font-mono text-sm font-semibold ${
                              repeatedRoomIds.has(row.roomId)
                                ? 'bg-amber-50 text-amber-900'
                                : 'bg-green-50 text-green-900'
                            }`}
                          >
                            {row.roomName}
                          </span>
                        )}
                        {repeatedRoomIds.has(row.roomId) && (
                          <p className="mt-1 text-sm font-medium text-amber-700">Repeated room</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          aria-label="Description"
                          className={cellInputClassName}
                          value={row.description}
                          onChange={(event) =>
                            updateRow(row.index, { description: event.target.value })
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          aria-label="Amount"
                          type="number"
                          min={0}
                          className={`${cellInputClassName} w-36 tabular-nums ${
                            Number(row.amount) > 0 ? '' : '!border-rose-500'
                          }`}
                          value={row.amount ?? ''}
                          onChange={(event) =>
                            updateRow(row.index, {
                              amount: event.target.value === '' ? null : Number(event.target.value)
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <ReactDatePicker
                          className={`${cellInputClassName} w-36`}
                          selected={row.paymentDate ? new Date(row.paymentDate) : null}
                          onChange={(date) =>
                            updateRow(row.index, {
                              paymentDate: date ? (date as Date).getTime() : null
                            })
                          }
                        />
                      </td>
                      {matchedRows.some((item) => item.page) && (
                        <td className="px-4 py-3 text-slate-700">{row.page ?? '—'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <footer className="flex shrink-0 flex-col-reverse items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-8 py-5 sm:flex-row">
          <button
            type="button"
            onClick={cancel}
            className="rounded-full px-5 py-2.5 text-base font-semibold text-slate-800 transition-colors hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canValidate}
            onClick={validate}
            className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-base font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {source === 'pdf' ? 'Validate PDF' : 'Validate lines'}
          </button>
          <button
            type="button"
            disabled={!canImport}
            onClick={importValid}
            className="flex items-center justify-center gap-2 rounded-full bg-[#185B43] px-6 py-2.5 text-base font-semibold text-white shadow-md shadow-[#185B43]/20 transition-all hover:bg-[#134935] focus:outline-none focus:ring-2 focus:ring-[#185B43]/50 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 disabled:shadow-none"
          >
            <span>
              {issueRows.length > 0
                ? `Add ${matchedRows.length} valid only`
                : `Add ${matchedRows.length} ${matchedRows.length === 1 ? 'payout' : 'payouts'}`}
            </span>
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </footer>
      </section>
    </Modal>
  );
};
